import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../../../store/hooks/StoreHooks";
import useCollapseFacade from "../../../../facade/collapse.Facade";
import Accordion from "../../../common/accordion";
import LABELS from "../../../../constants/labels/schematics.labels";
import useCalculationFacade from "../../../../facade/calculation/calculation.facade";
import Textarea from "../../../common/textarea";
import TextShortInput from "../../../common/TextShortInput";

interface RecommendProps {
    height?: number;
};

const RecommendedParameters: React.FC<RecommendProps> = ({ height }) => {
    const { recommnendedParametersCollapse } = useAppSelector((state) => state?.collapse);
    const { motorFrequencyDataForSchematics, schematicData } = useAppSelector(state => state?.calculations);
    const { tab3Input } = useAppSelector((state) => state?.fluid);

    const [freqData, setFreqData] = useState<any>([]);

    useEffect(() => {
        setFreqData({
            startupFrequency: schematicData?.miscellaneous?.startupFrequency ?? '',
            maxFrequency: schematicData?.miscellaneous?.maxFrequency ?? '',
            minFrequency: schematicData?.miscellaneous?.minFrequency ?? '',
            notes: schematicData?.miscellaneous?.note ?? '',
            previousMinFrequency: schematicData?.miscellaneous?.minFrequency ?? '',
            previousMaxFrequency: schematicData?.miscellaneous?.maxFrequency ?? '',
        })
    }, [])

    const { updateRecommendedParametersCollapse } = useCollapseFacade();
    const { fetchCalculatedSetDepth, updateSchematicOtherData, updateMotorLoadFrequency } = useCalculationFacade();

    const handleCollapseChange = (key: boolean) => {
        updateRecommendedParametersCollapse(key);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFreqData({ ...freqData, [event.target.name]: event.target.value })
        if ((event.target.name === "minFrequency" && event.target.value !== freqData?.previousMinFrequency)
            || event.target.name === "maxFrequency" && event.target.value !== freqData?.previousMaxFrequency) {
            setFreqData({
                ...freqData,
                previousMinFrequency: freqData?.minFrequency,
                previousMaxFrequency: freqData?.maxFrequency,
                [event.target.name]: event.target.value
            });
        }

        const misc = {
            note: event.target.name === "notes" ? event.target.value : freqData?.notes,
            startupFrequency: event.target.name === "startupFrequency" ? event.target.value : freqData?.startupFrequency,
            maxFrequency: event.target.name === "maxFrequency" ? event.target.value : freqData?.maxFrequency,
            minFrequency: event.target.name === "minFrequency" ? event.target.value : freqData?.minFrequency,
        };
        updateSchematicOtherData(misc);
        updateMotorLoadFrequency();
        fetchCalculatedSetDepth();
    }


    return (
        <Accordion
            title={LABELS?.recommendedParameters}
            isOpen={recommnendedParametersCollapse}
            onToggle={() => handleCollapseChange(!recommnendedParametersCollapse)}
            content={
                <div className="flex flex-col gap-[16px] p-4">
                    <div className="flex">
                        <div className='flex justify-start items-center gap-[8px] text-neutral-25'>
                            <TextShortInput
                                type='text'
                                name="startupFrequency"
                                className={`w-[60px] p-[8px] border-[1px] rounded-[8px] border-darkcolor-400 focus:outline-none bg-darkcolor-700 text-neutral-25 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500`}
                                value={freqData?.startupFrequency ?? ''}
                                onChange={(e) => handleChange(e)}
                            />
                            <label className="text-neutral-25">Hz</label>
                        </div>
                    </div>
                    <div className="flex text-neutral-25">{LABELS?.calculationsATDiffFreq}</div>
                    <div className={`scrollable max-h-[${height}] relative overflow-auto`}>
                        <table className='min-w-full rounded-[8px] outline-[1px] table'>
                            <thead style={{ background: "#253040" }}>
                                <tr>
                                    <th className='px-[24px] py-[16px] text-[12px] text-neutral-25'>
                                        <div></div>
                                    </th>
                                    <th className='px-[24px] py-[16px] text-[12px] text-neutral-25'>
                                        <div className="flex items-center">{LABELS?.minStart}</div>
                                    </th>
                                    <th className='px-[24px] py-[16px] text-[12px] text-neutral-25'>
                                        <div className="flex items-center">{LABELS?.design}</div>
                                    </th>
                                    <th className='px-[24px] py-[16px] text-[12px] text-neutral-25'>
                                        <div className="flex items-center">{LABELS?.max}</div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className='px-[24px] py-[9px] text-neutral-25 text-[14px]'>
                                        <div className='flex justify-start items-center gap-[8px] text-neutral-25'>
                                            <label>{LABELS?.frequency}</label>
                                        </div>
                                    </td>
                                    <td className='px-[24px] py-[9px] text-neutral-25 text-[14px]'>
                                        <div className='flex justify-start items-center gap-[8px] text-neutral-25'>
                                            <TextShortInput
                                                type='text'
                                                name="minFrequency"
                                                className={`w-[60px] p-[8px] border-[1px] rounded-[8px] border-darkcolor-400 focus:outline-none bg-darkcolor-700 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500`}
                                                value={freqData?.minFrequency ?? ''}
                                                onChange={(e) => handleChange(e)}
                                            />
                                            <label className="text-neutral-25">Hz</label>
                                        </div>
                                    </td>
                                    <td className='px-[24px] py-[9px] text-neutral-25 text-[14px]'>
                                        <div className='flex justify-start items-center gap-[8px] text-neutral-25'>
                                            <label>{tab3Input?.operatngFreq ?? ''} Hz</label>
                                        </div>
                                    </td>
                                    <td className='px-[24px] py-[9px] text-neutral-25 text-[14px]'>
                                        <div className='flex justify-start items-center gap-[8px] text-neutral-25'>
                                            <TextShortInput
                                                type='text'
                                                name="maxFrequency"
                                                className={`w-[60px] p-[8px] border-[1px] rounded-[8px] border-darkcolor-400 focus:outline-none bg-darkcolor-700 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500`}
                                                value={freqData?.maxFrequency ?? ''}
                                                onChange={(e) => handleChange(e)}
                                            />
                                            <label className="text-neutral-25">Hz</label>
                                        </div>
                                    </td>
                                </tr>
                                {motorFrequencyDataForSchematics?.map((res: any, idx: number) => (
                                    <React.Fragment key={idx}>
                                        <tr>
                                            <td className='px-[24px] py-[9px] text-neutral-25 text-[14px]'>
                                                <div className='flex justify-start items-center gap-[8px] text-neutral-25'>
                                                    <label>{res?.name ?? ''}</label>
                                                </div>
                                            </td>
                                            <td className='px-[24px] py-[9px] text-neutral-25 text-[14px]'>
                                                <div className='flex justify-start items-center gap-[8px] text-neutral-25'>
                                                    <label>{res?.min ?? ''}</label>
                                                </div>
                                            </td>
                                            <td className='px-[24px] py-[9px] text-neutral-25 text-[14px]'>
                                                <div className='flex justify-start items-center gap-[8px] text-neutral-25'>
                                                    <label>{res?.design ?? ''}</label>
                                                </div>
                                            </td>
                                            <td className='px-[24px] py-[9px] text-neutral-25 text-[14px]'>
                                                <div className='flex justify-start items-center gap-[8px] text-neutral-25'>
                                                    <label>{res?.max ?? ''}</label>
                                                </div>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                   <label className="text-neutral-25">Notes:</label>
                    <Textarea
                        placeholder="Write here"
                        name="notes"
                        value={freqData?.notes ?? ''}
                        rows={4}
                        onChange={(e) => handleChange(e)}
                        maxLength={300}
                    />
                </div>
            }
        />
    );
};

export default RecommendedParameters;
